import React from "react";
import { useNavigate } from "react-router";
import { resolveFleetCardViewModel, buildFleetSearchValue } from "../utils/resultsFleetCardViewModel.js";
import { buildRouteSequenceData } from "../utils/routeModelsData.js";
import { buildDetailedContext, buildErrorsCenterEntries, severityLabel } from "../utils/errorsCenterData.js";
import { applyDispatchPeriodSearchParam, normalizeDispatchPeriod } from "../utils/dispatchDateUrls.js";
import { formatTimelineClockLabel } from "../utils/fleetTimelinePresentation.js";
import { MobileEditor } from "./MobileRecordCard.jsx";
import RouteMapPanel from "./RouteMapPanel.jsx";

function formatRouteTime(timestamp, minutes) {
  if (minutes != null && minutes !== "" && Number.isFinite(Number(minutes))) return formatTimelineClockLabel(minutes);
  const date = timestamp ? new Date(timestamp) : null;
  return date && !Number.isNaN(date.getTime()) ? formatTimelineClockLabel(date.getHours() * 60 + date.getMinutes()) : "—";
}

function MobileTruckRoute({ card, expanded, onExpand, onOpenMap, state }) {
  const model = resolveFleetCardViewModel(card);
  const trips = Array.isArray(card.assignment?.trips) ? card.assignment.trips : [];
  return <article className="mobile-record-card" data-mobile-truck={model.assignmentId}>
    <h3>{model.shell.truckTitle}</h3>
    <div className="mobile-record-summary">
      <span>{model.shell.statusBadge?.label}</span>
      <span>Capacité : {model.shell.capacityLabel} · Charge maximale : {model.shell.peakLoadLabel}</span>
      <span>{model.distanceSummary}</span>
    </div>
    <button type="button" className="mobile-route-expand" aria-expanded={expanded} onClick={onExpand}>{expanded ? "Replier les arrêts" : "Voir les arrêts"}</button>
    {expanded ? <div className="mobile-route-trips">
      {trips.length === 0 ? <p>Aucune tournée pour ce camion.</p> : trips.map((trip, index) => {
        const sequence = buildRouteSequenceData(state, card.assignment, trip);
        return <section key={trip.trip_id || index}>
          <h4>Tournée {index + 1} · {sequence.displayedTotalDurationLabel}</h4>
          <button type="button" onClick={() => onOpenMap({ assignment: card.assignment, trip })}>Ouvrir la carte</button>
          <ol className="mobile-route-stops">
            {sequence.items.map((item, itemIndex) => <li key={`${item.operationKey || itemIndex}-${itemIndex}`}>
              <strong>{item.to?.typeLabel || item.operationTypeLabel || "Étape"} · {item.to?.label || item.operationLabel || "Arrêt"}</strong>
              <span>{item.to?.address}</span>
              <span>{item.productLabel}{item.to?.orderLabel ? ` · ${item.to.orderLabel}` : ""}</span>
              <span>Arrivée : {formatRouteTime(item.arrivalAt || item.scheduledAt, item.arrivalAtMinutes ?? item.scheduledAtMinutes)} · Fin : {formatRouteTime(item.segmentEndAt, item.segmentEndMinutes)}</span>
              {item.serviceDurationLabel ? <span>Service : {item.serviceDurationLabel}</span> : null}
            </li>)}
          </ol>
          {sequence.showEmptyState ? <p>Le détail des arrêts n’est pas disponible.</p> : null}
        </section>;
      })}
    </div> : null}
  </article>;
}

export default function MobileDispatch({ cards = [], result, state, stateRef }) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [expandedTruckId, setExpandedTruckId] = React.useState("");
  const [mapSelection, setMapSelection] = React.useState(null);
  const date = result?.dispatch_date || result?.planning_summary?.dispatch_date || "";
  const period = normalizeDispatchPeriod(result?.ui_dispatch_period || result?.dispatch_period);
  React.useEffect(() => { setMapSelection(null); }, [date, period]);
  const search = query.trim().toLocaleLowerCase("fr-CA");
  const visibleCards = cards.filter((card) => !search || buildFleetSearchValue(card.assignment).toLocaleLowerCase("fr-CA").includes(search));
  const alerts = React.useMemo(() => [
    ...buildErrorsCenterEntries(result, { viewName: "errors" }),
    ...buildErrorsCenterEntries(result, { viewName: "warnings" }),
  ], [result]);
  const openDate = (nextDate, nextPeriod) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) return;
    const params = new URLSearchParams({ dispatch_date: nextDate });
    applyDispatchPeriodSearchParam(params, nextPeriod);
    navigate(`/results?${params}`);
  };

  return <section className="mobile-dispatch" data-mobile-workspace="dispatch">
    <h1>Tournées par camion</h1>
    <div className="mobile-dispatch-filters">
      <label>Journée dispatch<input type="date" value={date} onChange={(event) => openDate(event.target.value, period)} /></label>
      <label>Période<select value={period} onChange={(event) => openDate(date, event.target.value)}><option value="day">Jour</option><option value="evening">Soir</option></select></label>
      <label>Rechercher un camion ou une commande<input type="search" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
    </div>
    <p role="status">{visibleCards.length} camion{visibleCards.length > 1 ? "s" : ""}</p>
    {alerts.length > 0 ? <details className="mobile-record-card"><summary>Alertes et avertissements · {alerts.length}</summary>
      <ul className="mobile-dispatch-alerts">{alerts.map((alert) => <li key={alert.errorId}>
        <strong>{severityLabel(alert.severity)} · {alert.title}</strong>
        <p>{buildDetailedContext(alert)}</p>
        <p>{alert.cause}</p>
        {alert.recommendedAction ? <p>{alert.recommendedAction}</p> : null}
      </li>)}</ul>
    </details> : null}
    {visibleCards.length === 0 ? <p>{cards.length ? "Aucun camion ne correspond à cette recherche." : "Aucune tournée disponible pour cette journée."}</p> : visibleCards.map((card) => <MobileTruckRoute key={card.assignmentId} card={card} expanded={expandedTruckId === card.assignmentId} state={state} onExpand={() => {
      setExpandedTruckId((current) => current === card.assignmentId ? "" : card.assignmentId);
      card.interactions?.onSelectAssignment?.(card.assignmentId);
    }} onOpenMap={setMapSelection} />)}
    {mapSelection ? <MobileEditor title={`Carte · ${mapSelection.assignment.truck_id}`} onClose={() => setMapSelection(null)}>
      <RouteMapPanel renderInline compact assignment={mapSelection.assignment} trip={mapSelection.trip} stateRef={stateRef} renderObservability={false} renderGroupRangeToggle={false} customMapHeight={Math.max(240, (window.visualViewport?.height || window.innerHeight) - 200)} />
    </MobileEditor> : null}
  </section>;
}
